import { useRef, useState } from 'react';

import { Divider, Modal, Text, type TypeSelectOption, dialogService, toastService } from '@thefarmersfront/kitchen';

import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { ModalContentsContainer } from '~/components/common';

import {
  deliveryAgencyKeys,
  useGetCollectingRequestCandidateDetailQuery,
  useGetCollectingRequestCandidateValidationMutation,
  useGetCollectingRequestCandidatesQuery,
  useGetCollectingRequestCandidatesSearchConditionsQuery,
  useGetCollectingRequestRegisterOptionsQuery,
  usePostCollectingRequestMutation,
  usePostCollectingRequestValidateAddressMutation,
} from '~/hooks/delivery-agency/queries';
import { addIf } from '~/utils/common';
import { formatDate } from '~/utils/date';

import { TOAST_MESSAGE } from '~/constants';
import {
  COLLECTING_CANDIDATE_COURIER_CODE,
  COLLECTING_CANDIDATE_DATE_REQUIRED_KEYWORD_TYPES,
  COLLECTING_CANDIDATE_ORDER_STATUS,
  INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
  INITIAL_DELIVERY_AGENCY_PAGE_SIZE,
  MAX_DELIVERY_AGENCY_DATE_RANGE,
} from '~/constants/delivery-agency';
import { DELIVERY_AGENCY } from '~/constants/errorCode';

import CollectingRequestCandidateFilter from '../CollectingRequestCandidateFilter';
import CollectingRequestCandidateTable from '../CollectingRequestCandidateTable';
import CollectingRequestRegisterStep2 from './CollectingRequestRegisterStep2';
import useCollectingRequestRegisterForm from './useCollectingRequestRegisterForm';

import type {
  CollectingOutboundListItem,
  CollectingRequestCandidateFilterOptions,
  CollectingRequestCandidateFilterValues,
  CollectingRequestCandidateFilterValuesStatus,
  CollectingSiteAddressValidation,
  GetCollectingRequestCandidatesParams,
  PostCollectingRequestParams,
} from '~/types/delivery-agency.types';

import styles from './CollectingRequestRegisterModal.module.scss';

const EMPTY_FILTER_OPTIONS: CollectingRequestCandidateFilterOptions = {
  searchKeywordTypes: [],
};

const INITIAL_FILTER_VALUES: CollectingRequestCandidateFilterValues = {
  keywordType: null,
  keyword: '',
  estimatedDeliveryDateRange: null,
  pagination: {
    currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
    pageSize: INITIAL_DELIVERY_AGENCY_PAGE_SIZE,
    totalCount: 0,
  },
};

const INITIAL_FILTER_VALUES_STATUS: CollectingRequestCandidateFilterValuesStatus = {
  keyword: 'default',
  estimatedDeliveryDateRange: 'default',
};

const EMPTY_REGISTER_OPTIONS = {
  deliveryLocations: [],
  entranceMethods: [],
  collectedProductHandlings: [],
};

const CollectingRequestRegisterModal = ({
  open,
  onClose,
  onRegistered,
}: {
  open: boolean;
  onClose: () => void;
  onRegistered: () => void;
}) => {
  const queryClient = useQueryClient();

  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [filterValues, setFilterValues] = useState(INITIAL_FILTER_VALUES);
  const [filterValuesStatus, setFilterValuesStatus] = useState(INITIAL_FILTER_VALUES_STATUS);
  const [candidatesQueryParams, setCandidatesQueryParams] = useState<GetCollectingRequestCandidatesParams | null>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<CollectingOutboundListItem | null>(null);
  const [registerOutboundCode, setRegisterOutboundCode] = useState<string | null>(null);

  const searchConditionsQuery = useGetCollectingRequestCandidatesSearchConditionsQuery({
    enabled: open,
    showGlobalErrorToast: false,
  });
  const candidatesQuery = useGetCollectingRequestCandidatesQuery({
    params: candidatesQueryParams,
    showGlobalDimOverlay: false,
  });

  const candidateDetailQuery = useGetCollectingRequestCandidateDetailQuery({ outboundCode: registerOutboundCode });
  const registerOptionsQuery = useGetCollectingRequestRegisterOptionsQuery({
    enabled: open,
    showGlobalErrorToast: false,
  });
  const validateCandidateMutation = useGetCollectingRequestCandidateValidationMutation();
  const validateAddressMutation = usePostCollectingRequestValidateAddressMutation();
  const registerMutation = usePostCollectingRequestMutation();

  const candidateDetail = candidateDetailQuery.data ?? null;
  const form = useCollectingRequestRegisterForm(candidateDetail);

  const filterOptions = searchConditionsQuery.data ?? EMPTY_FILTER_OPTIONS;
  const candidates = candidatesQuery.data?.data ?? null;
  const hasSearchResult = (candidates?.length ?? 0) > 0;

  const resolvedFilterValues = {
    ...filterValues,
    keywordType: filterValues.keywordType ?? filterOptions.searchKeywordTypes[0] ?? null,
  };

  const pagination = {
    currentPage: candidatesQuery.data?.pagination.cursor ?? filterValues.pagination.currentPage,
    pageSize: candidatesQuery.data?.pagination.listView ?? filterValues.pagination.pageSize,
    totalCount: candidatesQuery.data?.pagination.listTotal ?? 0,
  };

  const buildCandidatesParams = (
    values: CollectingRequestCandidateFilterValues
  ): GetCollectingRequestCandidatesParams => {
    const hasKeyword = Boolean(values.keywordType?.value && values.keyword);

    return {
      courierCode: COLLECTING_CANDIDATE_COURIER_CODE,
      orderStatus: COLLECTING_CANDIDATE_ORDER_STATUS,
      ...addIf(hasKeyword, 'searchKeywordType', values.keywordType?.value),
      ...addIf(hasKeyword, 'searchKeyword', values.keyword),
      ...addIf(
        Boolean(values.estimatedDeliveryDateRange),
        'startDate',
        formatDate(values.estimatedDeliveryDateRange?.start, 'YYYY-MM-DD') ?? undefined
      ),
      ...addIf(
        Boolean(values.estimatedDeliveryDateRange),
        'endDate',
        formatDate(values.estimatedDeliveryDateRange?.end, 'YYYY-MM-DD') ?? undefined
      ),
      cursor: values.pagination.currentPage,
      limit: values.pagination.pageSize,
    };
  };

  const applyCandidatesParams = (params: GetCollectingRequestCandidatesParams) => {
    setSelectedCandidate(null);
    queryClient.invalidateQueries({ queryKey: deliveryAgencyKeys.collectingRequestCandidates(params) });
    setCandidatesQueryParams(params);
  };

  const handleSearch = (values: CollectingRequestCandidateFilterValues) => {
    const hasKeyword = Boolean(values.keywordType?.value && values.keyword);
    const hasDateRange = Boolean(values.estimatedDeliveryDateRange);

    if (!hasKeyword && !hasDateRange) {
      setFilterValuesStatus((prev) => ({ ...prev, keyword: 'error' }));
      return;
    }

    const needsDateRange =
      values.keywordType?.value && COLLECTING_CANDIDATE_DATE_REQUIRED_KEYWORD_TYPES.includes(values.keywordType.value);

    if (needsDateRange && !hasDateRange) {
      setFilterValuesStatus((prev) => ({ ...prev, estimatedDeliveryDateRange: 'error' }));
      dialogService.addDialog({ type: 'alert', children: '배송예정일을 입력 후 검색해 주세요.' });
      return;
    }

    if (values.estimatedDeliveryDateRange) {
      const { start, end } = values.estimatedDeliveryDateRange;
      if (dayjs(end).diff(dayjs(start), 'day') + 1 > MAX_DELIVERY_AGENCY_DATE_RANGE) {
        setFilterValuesStatus((prev) => ({ ...prev, estimatedDeliveryDateRange: 'error' }));
        dialogService.addDialog({ type: 'alert', children: '최대 31일로 입력 후 검색해주세요.' });
        return;
      }
    }

    setFilterValues(values);
    setFilterValuesStatus(INITIAL_FILTER_VALUES_STATUS);
    applyCandidatesParams(buildCandidatesParams(values));
  };

  const handleResetFilterValues = () => {
    setFilterValues(INITIAL_FILTER_VALUES);
    setFilterValuesStatus(INITIAL_FILTER_VALUES_STATUS);
  };

  const handleChangePageLimit = (value: TypeSelectOption<number>) => {
    if (!candidatesQueryParams) return;

    setFilterValues((prev) => ({ ...prev, pagination: { ...prev.pagination, pageSize: value.value } }));
    applyCandidatesParams({
      ...candidatesQueryParams,
      cursor: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
      limit: value.value,
    });
  };

  const handleChangePage = (page: number) => {
    if (!candidatesQueryParams) return;

    applyCandidatesParams({ ...candidatesQueryParams, cursor: page });
  };

  const handleCancel = () => {
    if (!hasSearchResult) {
      onClose();
      return;
    }

    dialogService.addDialog({
      type: 'confirm',
      cancelText: '취소',
      confirmText: '나가기',
      title: '작성된 내용이 저장되지 않습니다',
      children: '페이지를 나가시겠습니까?',
      onConfirm: onClose,
    });
  };

  /** [이전]은 원주문을 다시 고르는 흐름이라 1step 조회 결과와 2step 입력을 모두 비운다 */
  const resetToCandidateSelection = () => {
    handleResetFilterValues();
    setCandidatesQueryParams(null);
    setSelectedCandidate(null);
    setRegisterOutboundCode(null);
    setCurrentStep(1);
  };

  const handleStepChange = (step: number) => {
    if (step !== 2) {
      if (currentStep === 2) {
        dialogService.addDialog({
          type: 'confirm',
          confirmText: '이전',
          title: '작성된 내용이 있을 경우 저장되지 않습니다',
          children: '회수 주문 선택 페이지로 이동하시겠습니까?',
          onConfirm: resetToCandidateSelection,
        });
      }
      return;
    }

    if (!selectedCandidate) {
      dialogService.addDialog({ type: 'alert', children: '반품 신청할 주문을 선택해주세요.' });
      return;
    }

    dialogService.addDialog({
      type: 'confirm',
      children: '반품 정보 입력을 위해 다음 페이지로 이동하시겠습니까?',
      onConfirm: () => validateCandidate(selectedCandidate.outboundCode),
    });
  };

  const goToStep2 = (outboundCode: string) => {
    setRegisterOutboundCode(outboundCode);
    setCurrentStep(2);
  };

  /** 정책서 '반품 접수' 플로우: 이동 확인 → 사전검증 → 경고가 있으면 한 번 더 확인하고 진행 */
  const validateCandidate = (outboundCode: string) => {
    validateCandidateMutation.mutate(
      { outboundCode },
      {
        onSuccess: ({ warnings }) => {
          if (warnings.length === 0) {
            goToStep2(outboundCode);
            return;
          }

          // 이전 접수 이력은 차단이 아니라 확인 대상이라, 확인을 받고 그대로 진행한다
          dialogService.addDialog({
            type: 'confirm',
            confirmText: '계속 신청',
            children: warnings.map((warning) => warning.message).join('\n\n'),
            onConfirm: () => goToStep2(outboundCode),
          });
        },
        onError: (error) => {
          dialogService.addDialog({ children: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
        },
      }
    );
  };

  // 늦게 도착한 이전 주소의 검증 응답이 현재 주소의 결과를 덮어쓰지 않도록 마지막 요청 주소만 반영한다
  const latestValidatedAddressRef = useRef<string | null>(null);

  /** 검색으로 고른 회수지 주소의 회수 가능 여부를 검증한다 */
  const validateAddress = (primaryAddress: string) => {
    latestValidatedAddressRef.current = primaryAddress;
    const isLatest = () => latestValidatedAddressRef.current === primaryAddress;

    validateAddressMutation.mutate(
      { primaryAddress },
      {
        onSuccess: (validation: CollectingSiteAddressValidation) => {
          if (!isLatest()) return;
          if (validation.available) {
            form.applyAddressValidation(validation);
            return;
          }
          // 정책서: 회수 불가 지역이면 얼럿으로 알리고 주소를 비워 다시 검색하게 한다
          dialogService.addDialog({ type: 'alert', children: validation.unserviceableReason });
          form.setValue('primaryAddress', '');
          form.applyAddressValidation(null);
        },
        // 검증 요청 자체가 실패하면 사유를 필드에 노출해 등록을 막는다. 검증 없이 등록되는 경로를 남기지 않는다
        onError: (error) => {
          if (!isLatest()) return;
          form.applyAddressValidation({
            available: false,
            unserviceableReason: error?.message || TOAST_MESSAGE.DEFAULT_ERROR,
          });
        },
      }
    );
  };

  const handleSearchAddress = (primaryAddress: string) => {
    form.handleCompleteAddressSearch(primaryAddress);
    validateAddress(primaryAddress);
  };

  const handleResetCollectingInfo = () => {
    dialogService.addDialog({
      type: 'confirm',
      confirmText: '초기화',
      title: '고객 연락처/ 회수지/ 회수장소/ 상품명 등 설정 영역에 입력된 값이 모두 지워집니다',
      children: '초기화를 진행하시겠습니까?',
      onConfirm: form.handleResetValues,
    });
  };

  const register = (params: PostCollectingRequestParams) => {
    registerMutation.mutate(params, {
      onSuccess: () => {
        toastService.addToast({ message: '반품 등록이 완료되었습니다.\n회수 운송장은 정상 접수되어 순차 발급됩니다.' });
        onRegistered();
        onClose();
      },
      onError: (error) => {
        if (
          error?.code === DELIVERY_AGENCY.BACKEND_ERROR_DIALOG_CODE ||
          error?.code === DELIVERY_AGENCY.COLLECTING_UNSERVICEABLE_AREA_CODE
        ) {
          dialogService.addDialog({ children: error?.message });
          return;
        }
        toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
      },
    });
  };

  /** 유효성 통과 후 컨펌을 거쳐 등록한다 (정책서 '반품 접수 프로세스') */
  const handleRegister = () => {
    const params = form.validate();
    if (!params) return;

    dialogService.addDialog({
      type: 'confirm',
      children: '반품 접수를 진행하시겠습니까?',
      onConfirm: () => register(params),
    });
  };

  return (
    <Modal
      title="반품 신청"
      size="xlarge"
      confirmText="등록"
      open={open}
      disabled={
        currentStep === 1 ? !hasSearchResult || validateCandidateMutation.isPending : validateAddressMutation.isPending
      }
      step={{
        steps: [{ title: '반품 주문 선택' }, { title: '반품 정보 입력' }],
        currentStep,
        onStepChange: handleStepChange,
      }}
      onCancel={handleCancel}
      onConfirm={handleRegister}
    >
      <ModalContentsContainer gap={currentStep === 2 ? 16 : 12}>
        {currentStep === 1 && (
          <>
            <div className={styles['guide-banner']}>
              <Text className={styles['guide-banner-text']} size="large" color="kurly" element="p">
                <Text size="large" color="kurly" element="span" bold>
                  넥스트마일 - 배송완료
                </Text>
                {
                  ' 주문만 반품 신청을 할 수 있습니다. 다른 배송사 주문의(CJ/롯데) 반품 신청을 원하실 경우 넥스트마일 담당자에게 문의해주세요.'
                }
              </Text>
            </div>
            <CollectingRequestCandidateFilter
              options={filterOptions}
              values={resolvedFilterValues}
              valuesStatus={filterValuesStatus}
              onChange={(values) => setFilterValues((prev) => ({ ...prev, ...values }))}
              onChangeStatus={(status) => setFilterValuesStatus((prev) => ({ ...prev, ...status }))}
              onSearch={() =>
                handleSearch({
                  ...resolvedFilterValues,
                  pagination: { ...resolvedFilterValues.pagination, currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR },
                })
              }
              onReset={handleResetFilterValues}
            />
            <Divider color="gray100" />
            <CollectingRequestCandidateTable
              data={candidates}
              pagination={pagination}
              onChangePageLimit={handleChangePageLimit}
              onChangePage={handleChangePage}
              onChangeRowSelection={(rows) => setSelectedCandidate(rows[0] ?? null)}
            />
          </>
        )}
        {currentStep === 2 && candidateDetail && (
          <CollectingRequestRegisterStep2
            detail={candidateDetail}
            options={registerOptionsQuery.data ?? EMPTY_REGISTER_OPTIONS}
            form={form}
            onSearchAddress={handleSearchAddress}
            onResetCollectingInfo={handleResetCollectingInfo}
          />
        )}
      </ModalContentsContainer>
    </Modal>
  );
};

export default CollectingRequestRegisterModal;
